import * as C from '../constants';
import defaultState from './defaultState';
import FerryService from '../../../services/transportation-service';

export default {
  /**
   * @param commit
   */
  clearState({ commit }) {
    commit(C.TRANSPORTATION_CLEAR_STATE);
  },

  /**
   * Set the active tab.
   *
   * @param commit
   * @param payload
   */
  setActiveTab({ commit }, payload) {
    commit(C.TRANSPORTATION_ACTIVE_TAB_SET, payload);
  },

  /**
    TRANSPORTATION SUPPLIERS
   */

  /**
   * Retrieve suppliers for transportation type.
   *
   * @param commit
   * @param payload
   * @returns {Promise<any | never>}
   */
  suppliersLoad({ commit }) {
    commit(C.TRANSPORTATION_SUPPLIERS_LOAD_PENDING);
    return FerryService.getSuppliers()
      .then((res) => {
        commit(C.TRANSPORTATION_SUPPLIERS_LOAD_SUCCESS, res.data);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_SUPPLIERS_LOAD_FAILURE, err.response.data);
      });
  },

  /**
   * Change the current chosen supplier.
   *
   * @param commit
   * @param payload
   */
  suppliersSet({ commit }, payload) {
    commit(C.TRANSPORTATION_SUPPLIERS_CHANGE, payload);
  },

  /**
   TRANSPORTATION CAR TYPES
   */

  /**
   * Search for car types.
   *
   * @param commit
   * @param payload
   */
  carTypesSearch({ commit }, payload) {
    commit(C.TRANSPORTATION_CAR_TYPES_SEARCH, payload);
  },

  /**
   * Update pagination for car types.
   *
   * @param commit
   * @param payload
   */
  carTypesUpdatePagination({ commit }, payload) {
    commit(C.TRANSPORTATION_CAR_TYPES_PAGINATION_CHANGED, payload);
  },

  /**
   * Set active car type.
   *
   * @param commit
   * @param payload
   */
  carTypesSetActive({ commit }, payload) {
    commit(C.TRANSPORTATION_CAR_TYPES_SET_ACTIVE, { ...payload });
  },

  /**
   * Retrieve car types for the chosen supplier.
   *
   * @param commit
   * @param state
   */
  carTypesLoad({ commit, state }) {
    commit(C.TRANSPORTATION_CAR_TYPES_LOAD_PENDING);
    const supplier = state.suppliers.items.find(
      item => item.id === state.suppliers.supplier,
    );
    return FerryService.getCarTypes(supplier.id)
      .then((res) => {
        if (res.data) {
          commit(C.TRANSPORTATION_CAR_TYPES_LOAD_SUCCESS, res.data);
        } else {
          commit(C.TRANSPORTATION_CAR_TYPES_LOAD_NO_CONTENT);
        }
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_CAR_TYPES_LOAD_FAILURE, err.response.data);
      });
  },

  /**
   * Save a car type.
   *
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  carTypesSave({ commit, state, dispatch }, payload) {
    commit(C.TRANSPORTATION_CAR_TYPES_SAVE_PENDING);
    return FerryService.saveCarType(payload)
      .then(() => {
        const index = state.carTypes.items.findIndex(
          item => item.id === state.carTypes.carType.id,
        );
        const items = [...state.carTypes.items];
        items.splice(index, 1, payload);
        commit(C.TRANSPORTATION_CAR_TYPES_SAVE_SUCCESS, items);
        dispatch('carTypesSetActive', payload);
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_CAR_TYPES_SAVE_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  carTypesAdd({ commit, state, dispatch }, payload) {
    commit(C.TRANSPORTATION_CAR_TYPES_ADD_PENDING);
    return FerryService.addCarType(state.suppliers.supplier, payload)
      .then(() => {
        commit(C.TRANSPORTATION_CAR_TYPES_ADD_SUCCESS);
        dispatch('carTypesLoad');
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_CAR_TYPES_ADD_FAILURE, err.response.data);
      });
  },

  /**
   * TRANSPORTATION ROUTES
   */

  /**
   * Search for routes.
   *
   * @param commit
   * @param payload
   */
  routesSearch({ commit }, payload) {
    commit(C.TRANSPORTATION_ROUTES_SEARCH, payload);
  },

  /**
   * Change pagination for routes.
   *
   * @param commit
   * @param payload
   */
  routesUpdatePagination({ commit }, payload) {
    commit(C.TRANSPORTATION_ROUTES_PAGINATION_CHANGED, payload);
  },

  /**
   * Set the active route.
   *
   * @param commit
   * @param payload
   */
  routesSetActive({ commit }, payload) {
    commit(C.TRANSPORTATION_ROUTES_SET_ACTIVE, Object.assign({}, payload));
  },

  /**
   * Retrieve a list of routes.
   *
   * @param commit
   * @param state
   * @returns {Promise<any | never>}
   */
  routesLoad({ commit, state }) {
    commit(C.TRANSPORTATION_CABINS_ITEMS_CLEAR);
    commit(C.TRANSPORTATION_EXTRAS_ITEMS_CLEAR);
    commit(C.TRANSPORTATION_PRICES_CLEAR);
    if (!state.suppliers.supplier) {
      return Promise.resolve();
    }
    commit(C.TRANSPORTATION_ROUTES_LOAD_PENDING);
    return FerryService.getRoutes(state.suppliers.supplier)
      .then((res) => {
        if (res.data) {
          commit(C.TRANSPORTATION_ROUTES_LOAD_SUCCESS, res.data);
        } else {
          commit(C.TRANSPORTATION_ROUTES_LOAD_NO_CONTENT);
        }
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_ROUTES_LOAD_FAILURE, err.response.data);
      });
  },

  /**
   * Save route areas.
   *
   * @param commit
   * @param state
   * @returns {Promise<T | never>}
   */
  routesSaveAreas({ commit, state }) {
    commit(C.TRANSPORTATION_ROUTES_SAVE_PENDING);
    const areaIds = state.routes.areas.reduce((carry, area) => {
      if (!area.deleted) {
        carry.push(area.id);
      }
      return carry;
    }, []);
    const route = Object.assign({}, state.routes.route);
    route.areaIds = areaIds;

    return FerryService.saveRoute(route)
      .then(() => {
        const index = state.routes.items.findIndex(item => item.id === state.routes.route.id);
        const items = [...state.routes.items];
        items.splice(index, 1, route);
        commit(C.TRANSPORTATION_ROUTES_SAVE_SUCCESS, items);
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_ROUTES_SAVE_FAILURE, err.response.data);
      });
  },

  /**
   * Save a route.
   *
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   * @returns {Promise<T | never>}
   */
  routesSave({ commit, state, dispatch }, payload) {
    commit(C.TRANSPORTATION_ROUTES_SAVE_PENDING);
    return FerryService.saveRoute(payload)
      .then(() => {
        const index = state.routes.items.findIndex(item => item.id === payload.id);
        const items = [...state.routes.items];
        items.splice(index, 1, payload);
        commit(C.TRANSPORTATION_ROUTES_SAVE_SUCCESS, items);
        dispatch('routesSetActive', payload);
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_ROUTES_SAVE_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @returns {Promise.<T>}
   */
  routeLoadCheckInInformation({ commit, state }) {
    commit(C.TRANSPORTATION_ROUTES_CHECK_IN_INFORMATION_LOAD_PENDING);
    const checkInInformations = defaultState().routes.checkInInformations;
    return FerryService.getCheckInInformationForRoute(state.routes.route)
      .then((res) => {
        if (res.data) {
          if (res.data.checkInInformations) {
            Object.keys(res.data.checkInInformations).forEach((id) => {
              if (res.data.checkInInformations[id].outbound) {
                checkInInformations.outbound = res.data.checkInInformations[id];
              } else {
                checkInInformations.inbound = res.data.checkInInformations[id];
              }
            });
          }
          commit(C.TRANSPORTATION_ROUTES_CHECK_IN_INFORMATION_LOAD_SUCCESS, checkInInformations);
        } else {
          commit(C.TRANSPORTATION_ROUTES_CHECK_IN_INFORMATION_LOAD_NO_CONTENT, checkInInformations);
        }
      })
      .catch((err) => {
        commit(
          C.TRANSPORTATION_ROUTES_CHECK_IN_INFORMATION_LOAD_FAILURE,
          err.response.data,
          checkInInformations,
        );
      });
  },

  /**
   * @param commit
   * @param state
   * @param payload
   * @returns {Promise.<TResult>}
   */
  routeSaveCheckInInformation({ commit, state }, payload) {
    commit(C.TRANSPORTATION_ROUTES_CHECK_IN_INFORMATION_SAVE_PENDING);
    const promises = [];
    promises.push(FerryService.saveCheckInInformationForRoute(
      state.routes.route,
      payload.outbound,
    ));
    promises.push(FerryService.saveCheckInInformationForRoute(
      state.routes.route,
      payload.inbound,
    ));
    return Promise.all(promises).then(() => {
      commit(C.TRANSPORTATION_ROUTES_CHECK_IN_INFORMATION_SAVE_SUCCESS);
    }).catch((err) => {
      commit(C.TRANSPORTATION_ROUTES_CHECK_IN_INFORMATION_SAVE_FAILURE, err.response.data);
      throw new Error();
    });
  },

  /**
   * Retrieve a list of areas mapped to a route.
   *
   * @param commit
   * @param state
   * @returns {Promise<T | never>}
   */
  routesLoadAreas({ commit, state }) {
    commit(C.TRANSPORTATION_ROUTES_AREAS_LOAD_PENDING);
    return FerryService.getRouteAreas(state.routes.route)
      .then((res) => {
        commit(C.TRANSPORTATION_ROUTES_AREAS_LOAD_SUCCESS, res.data);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_ROUTES_AREAS_LOAD_FAILURE, err.response.data);
      });
  },

  /**
   * Add an area to a route.
   *
   * @param commit
   * @param state
   * @param payload
   */
  routesAddArea({ commit, state }, payload) {
    const index = state.routes.areas.findIndex(area => area.id === payload.id);
    if (index !== -1) {
      commit(C.TRANSPORTATION_ROUTES_AREAS_TOGGLE, index);
    } else {
      const area = Object.assign({}, payload);
      area.new = true;
      commit(C.TRANSPORTATION_ROUTES_AREAS_ADD, area);
    }
  },

  /**
   * Remove an area to a route.
   *
   * @param commit
   * @param state
   * @param payload
   */
  routesRemoveArea({ commit, state }, payload) {
    const index = state.routes.areas.findIndex(area => area.id === payload.id);
    if (payload.new) {
      commit(C.TRANSPORTATION_ROUTES_AREAS_DELETE, index);
    } else {
      commit(C.TRANSPORTATION_ROUTES_AREAS_TOGGLE, index);
    }
  },

  /**
   * Add all areas to a route.
   *
   * @param commit
   * @param state
   * @param payload
   */
  routesAddAllAreas({ commit, state }, payload) {
    const areas = [...state.routes.areas];
    payload.forEach((area) => {
      const index = areas.findIndex(item => item.id === area.id);
      if (index !== -1) {
        const newArea = Object.assign({}, areas[index]);
        newArea.deleted = false;
        areas[index] = newArea;
      } else {
        areas.push(Object.assign({ new: true }, area));
      }
    });
    commit(C.TRANSPORTATION_ROUTES_AREAS_REPLACE_ALL, areas);
  },

  /**
   * Remove all areas to a route.
   *
   * @param commit
   * @param state
   * @param payload
   */
  routesRemoveAllAreas({ commit, state }, payload) {
    const areas = [...state.routes.areas];
    payload.forEach((area) => {
      const index = areas.findIndex(item => item.id === area.id);
      if (area.new) {
        areas.splice(index, 1);
      } else {
        const newArea = Object.assign({}, areas[index]);
        newArea.deleted = true;
        areas[index] = newArea;
      }
    });
    commit(C.TRANSPORTATION_ROUTES_AREAS_REPLACE_ALL, areas);
  },

  /**
   * @param dispatch
   * @param routeId
   */
  getRouteTextsAndTags({ dispatch }, routeId) {
    dispatch('getRouteTextTags', routeId);
    dispatch('getRouteTexts', routeId);
  },

  /**
   * @param commit
   * @param routeId
   */
  getRouteTextTags({ commit }, routeId) {
    commit(C.TRANSPORTATION_ROUTES_TEXT_TAGS_LOAD_PENDING);
    return FerryService.getRouteTextTags(routeId)
      .then((res) => {
        if (res.status === 200) {
          commit(C.TRANSPORTATION_ROUTES_TEXT_TAGS_LOAD_SUCCESS, res.data);
        } else {
          commit(C.TRANSPORTATION_ROUTES_TEXT_TAGS_LOAD_FAILURE);
        }
      })
      .catch(() => {
        commit(C.TRANSPORTATION_ROUTES_TEXT_TAGS_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @param routeId
   * @returns {Promise<unknown>}
   */
  getRouteTexts({ commit }, routeId) {
    commit(C.TRANSPORTATION_ROUTES_TEXTS_LOAD_PENDING);
    return FerryService.getRouteTexts(routeId)
      .then((res) => {
        if (res.status === 200) {
          commit(C.TRANSPORTATION_ROUTES_TEXTS_LOAD_SUCCESS, res.data);
        } else if (res.status === 204) {
          commit(C.TRANSPORTATION_ROUTES_TEXTS_LOAD_SUCCESS, []);
        } else {
          commit(C.TRANSPORTATION_ROUTES_TEXTS_LOAD_FAILURE, 'Error with retrieval of route texts');
        }
      })
      .catch(() => {
        commit(C.TRANSPORTATION_ROUTES_TEXTS_LOAD_FAILURE, 'Something unexpected went wrong');
      });
  },

  /**
   * @param commit
   * @param routeId
   * @param text
   * @returns {Promise<unknown>}
   */
  createRouteText({ commit, dispatch }, data) {
    commit(C.TRANSPORTATION_ROUTES_CREATE_TEXT_PENDING);
    return FerryService.createRouteText(data.routeId, data.text)
      .then((res) => {
        if (res.status === 204) {
          commit(C.TRANSPORTATION_ROUTES_CREATE_TEXT_SUCCESS);
          dispatch('getRouteTextsAndTags', data.routeId);
        } else {
          commit(C.TRANSPORTATION_ROUTES_CREATE_TEXT_FAILURE);
        }
      })
      .catch(() => {
        commit(C.TRANSPORTATION_ROUTES_CREATE_TEXT_FAILURE);
      });
  },

  /**
   * @param commit
   * @param routeId
   * @param textId
   * @param text
   * @returns {Promise<unknown>}
   */
  updateRouteText({ commit, dispatch }, data) {
    commit(C.TRANSPORTATION_ROUTES_UPDATE_TEXT_PENDING);
    return FerryService.updateRouteText(data.routeId, data.textId, data.body)
      .then((res) => {
        if (res.status === 204) {
          commit(C.TRANSPORTATION_ROUTES_UPDATE_TEXT_SUCCESS);
          dispatch('getRouteTextsAndTags', data.routeId);
        } else {
          commit(C.TRANSPORTATION_ROUTES_UPDATE_TEXT_FAILURE);
        }
      })
      .catch(() => {
        commit(C.TRANSPORTATION_ROUTES_UPDATE_TEXT_FAILURE);
      });
  },

  /**
   * @param commit
   * @param routeId
   * @param textId
   * @returns {Promise<unknown>}
   */
  deleteRouteText({ commit, dispatch }, data) {
    commit(C.TRANSPORTATION_ROUTES_DELETE_TEXT_PENDING);
    return FerryService.deleteRouteText(data.routeId, data.textId)
      .then((res) => {
        if (res.status === 204) {
          commit(C.TRANSPORTATION_ROUTES_DELETE_TEXT_SUCCESS);
          dispatch('getRouteTextsAndTags', data.routeId);
        } else {
          commit(C.TRANSPORTATION_ROUTES_DELETE_TEXT_FAILURE);
        }
      })
      .catch(() => {
        commit(C.TRANSPORTATION_ROUTES_DELETE_TEXT_FAILURE);
      });
  },

  changeRouteText({ commit, state }, text) {
    const items = state.routes.texts.items.map((item) => {
      if (item.id === text.id) {
        return text;
      }
      return item;
    });
    commit(C.TRANSPORTATION_ROUTES_CHANGE_TEXT, items);
  },

  clearRouteTextsList({ commit }) {
    commit(C.TRANSPORTATION_ROUTES_TEXTS_CLEAR_LIST);
  },


  /**
   * TRANSPORTATION PERSON CATEGORIES
   */

  /**
   * @param commit
   * @param state
   */
  getPersonCategoriesBySupplier({ commit, state }) {
    commit(C.TRANSPORTATION_PERSON_CATEGORIES_LOAD_PENDING);
    return FerryService.getPersonCategoriesBySupplier(state.suppliers.supplier)
      .then((res) => {
        if (res.data) {
          commit(C.TRANSPORTATION_PERSON_CATEGORIES_LOAD_SUCCESS, res.data);
        } else {
          commit(C.TRANSPORTATION_PERSON_CATEGORIES_LOAD_NO_CONTENT);
        }
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PERSON_CATEGORIES_LOAD_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param payload
   */
  setPersonCategorySearch({ commit }, payload) {
    commit(C.TRANSPORTATION_PERSON_CATEGORIES_SEARCH, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setActivePersonCategory({ commit }, payload) {
    commit(C.TRANSPORTATION_PERSON_CATEGORIES_SET_ACTIVE, { ...payload });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  savePersonCategory({ commit, state, dispatch }, payload) {
    commit(C.TRANSPORTATION_PERSON_CATEGORIES_SAVE_PENDING);
    return FerryService.savePersonCategoryBySupplier(state.suppliers.supplier, payload)
      .then(() => {
        const index = state.personCategories.items.findIndex(
          item => item.id === state.personCategories.personCategory.id,
        );
        const items = [...state.personCategories.items];
        items.splice(index, 1, payload);
        commit(C.TRANSPORTATION_PERSON_CATEGORIES_SAVE_SUCCESS, items);
        dispatch('setActivePersonCategory', payload);
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PERSON_CATEGORIES_SAVE_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  addPersonCategory({ commit, state, dispatch }, payload) {
    commit(C.TRANSPORTATION_PERSON_CATEGORIES_ADD_PENDING);
    return FerryService.addPersonCategoryBySupplier(state.suppliers.supplier, payload)
      .then(() => {
        commit(C.TRANSPORTATION_PERSON_CATEGORIES_ADD_SUCCESS);
        dispatch('getPersonCategoriesBySupplier');
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PERSON_CATEGORIES_ADD_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param payload
   */
  updatePersonCategoryPagination({ commit }, payload) {
    commit(C.TRANSPORTATION_PERSON_CATEGORIES_PAGINATION_CHANGED, payload);
  },

  /**
   * TRANSPORTATION CABINS
   */

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  addCabinByRoute({ commit, state, dispatch }, payload) {
    commit(C.TRANSPORTATION_CABINS_ADD_PENDING);
    return FerryService.addCabinByRoute(state.cabins.route, payload)
      .then(() => {
        commit(C.TRANSPORTATION_CABINS_ADD_SUCCESS);
        dispatch('getCabinsByRoute');
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_CABINS_ADD_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  updateCabin({ commit, state, dispatch }, payload) {
    commit(C.TRANSPORTATION_CABINS_SAVE_PENDING);
    return FerryService.updateCabin(payload)
      .then(() => {
        const index = state.cabins.items.findIndex(
          item => item.id === state.cabins.cabin.id,
        );
        const items = [...state.cabins.items];
        items.splice(index, 1, payload);
        commit(C.TRANSPORTATION_CABINS_SAVE_SUCCESS, items);
        dispatch('setActiveCabin', payload);
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_CABINS_SAVE_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  getCabinsByRoute({ commit, state }) {
    commit(C.TRANSPORTATION_CABINS_LOAD_PENDING);
    return FerryService.getCabinsByRoute(state.cabins.route)
      .then((res) => {
        if (res.data) {
          commit(C.TRANSPORTATION_CABINS_LOAD_SUCCESS, res.data);
        } else {
          commit(C.TRANSPORTATION_CABINS_LOAD_NO_CONTENT);
        }
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_CABINS_LOAD_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param payload
   */
  setActiveCabin({ commit }, payload) {
    commit(C.TRANSPORTATION_CABINS_SET_ACTIVE, { ...payload });
  },

  /**
   * @param commit
   * @param payload
   */
  setCabinsSearch({ commit }, payload) {
    commit(C.TRANSPORTATION_CABINS_SEARCH, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   */
  setCabinRoute({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_CABINS_SET_ROUTE, payload);
    dispatch('getCabinsByRoute');
  },

  /**
   * TRANSPORTATION EXTRAS
   */
  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  addExtraByRoute({ commit, state, dispatch }, payload) {
    commit(C.TRANSPORTATION_EXTRAS_ADD_PENDING);
    return FerryService.addExtraByRoute(state.extras.route, payload)
      .then(() => {
        commit(C.TRANSPORTATION_EXTRAS_ADD_SUCCESS);
        dispatch('getExtrasByRoute');
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_EXTRAS_ADD_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param payload
   */
  setActiveExtra({ commit }, payload) {
    commit(C.TRANSPORTATION_EXTRAS_SET_ACTIVE, { ...payload });
  },

  /**
   * @param commit
   * @param payload
   */
  setExtrasSearch({ commit }, payload) {
    commit(C.TRANSPORTATION_EXTRAS_SEARCH, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   */
  setExtraRoute({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_EXTRAS_SET_ROUTE, payload);
    dispatch('getExtrasByRoute');
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  updateExtra({ commit, state, dispatch }, payload) {
    commit(C.TRANSPORTATION_EXTRAS_SAVE_PENDING);
    return FerryService.updateExtra(payload)
      .then(() => {
        const index = state.extras.items.findIndex(
          item => item.id === state.extras.extra.id,
        );
        const items = [...state.extras.items];
        items.splice(index, 1, payload);
        commit(C.TRANSPORTATION_EXTRAS_SAVE_SUCCESS, items);
        dispatch('setActiveExtra', payload);
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_EXTRAS_SAVE_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  getExtrasByRoute({ commit, state }) {
    commit(C.TRANSPORTATION_EXTRAS_LOAD_PENDING);
    return FerryService.getExtrasByRoute(state.extras.route)
      .then((res) => {
        if (res.data) {
          commit(C.TRANSPORTATION_EXTRAS_LOAD_SUCCESS, res.data);
        } else {
          commit(C.TRANSPORTATION_EXTRAS_LOAD_NO_CONTENT);
        }
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_EXTRAS_LOAD_FAILURE, err.response.data);
      });
  },

  /**
   * TRANSPORTATION PRICES
   */

  /**
   * @param commit
   * @param dispatch
   * @param payload
   */
  setFerryRoute({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_UNSET_SEASON);
    commit(C.TRANSPORTATION_PRICES_SET_ROUTE, payload);
    dispatch('getFerrySeasonsByRoute');
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   */
  setPricesRoute({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_UNSET_SEASON);
    commit(C.TRANSPORTATION_PRICES_SET_ROUTE, payload);
    dispatch('getSeasonsByRoute');
  },

  /**
   * TRANSPORTATION PRICES SEASONS
   */

  /**
   * @param commit
   * @param state
   * @param dispatch
   */
  getFerrySeasonsByRoute({ commit, state }) {
    commit(C.TRANSPORTATION_PRICES_SEASONS_FERRY_LOAD_PENDING);
    return FerryService.getFerrySeasonsByRoute(state.prices.route)
      .then((res) => {
        if (res.data) {
          commit(C.TRANSPORTATION_PRICES_SEASONS_FERRY_LOAD_SUCCESS, res.data);
        } else {
          commit(C.TRANSPORTATION_PRICES_SEASONS_FERRY_LOAD_NO_CONTENT);
        }
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PRICES_SEASONS_FERRY_LOAD_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   */
  setFerrySeason({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_SET_FERRY_SEASON, payload);
    dispatch('getFerrySeasonPrices');
  },

  /**
   * @param commit
   * @param state
   * @param payload
   * @param dispatch
   * TODO: RENAME DEPARTURE
   */
  addFerrySeason({ commit, state, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_SEASON_FERRY_ADD_PENDING);
    const season = {
      ...payload,
      routeId: state.prices.route,
    };
    return FerryService.addFjordlineSeason(season)
      .then((res) => {
        commit(C.TRANSPORTATION_PRICES_SEASON_FERRY_ADD_SUCCESS);
        dispatch('setFerrySeason', res.data.id);
        dispatch('getFerrySeasonsByRoute');
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PRICES_SEASON_FERRY_ADD_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param payload
   * TODO: LOOK AT IT
   */
  updateFerrySeason({ commit, state }, payload) {
    commit(C.TRANSPORTATION_PRICES_SEASON_FERRY_UPDATE_PENDING);
    const season = {
      ...payload,
      id: state.prices.season.selected,
      routeId: state.prices.route,
    };
    return FerryService.updateFerrySeason(season)
      .then(() => {
        commit(C.TRANSPORTATION_PRICES_SEASON_FERRY_UPDATE_SUCCESS);
        const seasonIndex = state.prices.season.items.findIndex(
          item => item.id === state.prices.season.selected,
        );
        commit(C.TRANSPORTATION_PRICES_SEASONS_LIST_UPDATE, {
          ...season,
          index: seasonIndex,
        });
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PRICES_SEASON_FERRY_UPDATE_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * TODO: NOW NAMED DEPARTURE
   */
  getFerrySeasonPrices({ commit, state }) {
    commit(C.TRANSPORTATION_PRICES_SEASON_FERRY_LOAD_PENDING);
    return FerryService.getFerrySeasonPrices(state.prices.season.selected)
      .then((res) => {
        if (res.data) {
          commit(C.TRANSPORTATION_PRICES_SEASON_FERRY_LOAD_SUCCESS, res.data);
        } else {
          commit(C.TRANSPORTATION_PRICES_SEASON_FERRY_LOAD_NO_CONTENT);
        }
        return Promise.resolve(true);
      })
      .catch(() => {
        commit(C.TRANSPORTATION_PRICES_SEASON_FERRY_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @param state
   * @param payload
   * @param dispatch
   * TODO: RENAME DEPARTURE
   */
  saveFerrySeasonPrices({ commit, state }, payload) {
    commit(C.TRANSPORTATION_PRICES_SEASON_FERRY_SAVE_PRICE_PENDING);
    return FerryService.saveFerrySeasonPrices(
      state.prices.season.selected,
      payload,
    ).then(() => {
      commit(C.TRANSPORTATION_PRICES_SEASON_FERRY_SAVE_PRICE_SUCCESS);
      return Promise.resolve(true);
    }).catch((err) => {
      commit(C.TRANSPORTATION_PRICES_SEASON_FERRY_SAVE_PRICE_FAILURE, err.response.data);
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   */
  getSeasonsByRoute({ commit, state }) {
    commit(C.TRANSPORTATION_PRICES_SEASONS_LOAD_PENDING);

    return FerryService.getSeasonsByRoute(state.prices.route)
      .then((res) => {
        if (res.data) {
          commit(C.TRANSPORTATION_PRICES_SEASONS_LOAD_SUCCESS, res.data);
        } else {
          commit(C.TRANSPORTATION_PRICES_SEASONS_LOAD_NO_CONTENT);
        }
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PRICES_SEASONS_LOAD_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param payload
   * @param dispatch
   * TODO: RENAME / GIVE ATTENTION
   */
  addSeason({ commit, state, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_SEASON_ADD_PENDING);
    const season = {
      ...payload,
      ferryRouteId: state.prices.route,
    };
    return FerryService.addSeason(season)
      .then((res) => {
        commit(C.TRANSPORTATION_PRICES_SEASON_ADD_SUCCESS);
        const seasonId = res.data;
        dispatch('setSeason', seasonId);
        dispatch('getSeasonsByRoute');
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PRICES_SEASON_ADD_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  updateSeason({ commit, state, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_SEASON_UPDATE_PENDING);
    const season = {
      ...payload,
      id: state.prices.season.selected,
      routeId: state.prices.route,
    };
    return FerryService.updateSeason(season)
      .then(() => {
        commit(C.TRANSPORTATION_PRICES_SEASON_UPDATE_SUCCESS);
        const seasonIndex = state.prices.season.items.findIndex(
          item => item.id === state.prices.season.selected,
        );
        commit(C.TRANSPORTATION_PRICES_SEASONS_LIST_UPDATE, {
          ...season,
          index: seasonIndex,
        });
        dispatch('getSeasonsByRoute');

        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PRICES_SEASON_UPDATE_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   */
  setSeason({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_SET_SEASON, payload);
    dispatch('getSeasonDepartures');
  },

  /**
   * @param commit
   */
  unsetSeason({ commit }) {
    commit(C.TRANSPORTATION_PRICES_UNSET_SEASON);
  },


  /**
   * TRANSPORTATION PRICES DEPARTURES
   */

  /**
   * @param commit
   * @param seasonPrice
   * TODO: RENAME
   */
  setSeasonDeparture({ commit }, seasonPrice) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_SET, seasonPrice);
  },

  /**
   * @param commit
   * @param direction
   * TODO: RENAME
   */
  setSeasonDepartureDirection({ commit }, direction) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_SET_DIRECTION, direction);
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * TODO: NOW NAMED DEPARTURE
   */
  getSeasonDepartures({ commit, state }) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURES_PENDING);
    return FerryService.getSeasonDepartures(state.prices.season.selected)
      .then((res) => {
        if (res.data) {
          commit(C.TRANSPORTATION_PRICES_DEPARTURES_SUCCESS, res.data);
        } else {
          commit(C.TRANSPORTATION_PRICES_DEPARTURES_NO_CONTENT);
        }
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURES_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param seasonDepartureId
   */
  deleteSeasonDeparture({ commit, dispatch }, seasonDepartureId) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_DELETE_PENDING);
    return FerryService.deleteSeasonDeparture(seasonDepartureId)
      .then(() => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_DELETE_SUCCESS, seasonDepartureId);
        dispatch('getSeasonDepartures');
        return Promise.resolve(true);
      })
      .catch((error) => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_DELETE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param payload
   * @param dispatch
   * TODO: RENAME DEPARTURE ADD
   */
  addSeasonDeparture({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_ADD_PENDING);
    return FerryService.addSeasonDeparture(payload)
      .then(() => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_ADD_SUCCESS);
        dispatch('getSeasonDepartures');
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_ADD_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param payload
   * @param dispatch
   * TODO: RENAME DEPARTURE
   */
  updateSeasonDeparture({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_SAVE_PENDING);
    return FerryService.updateSeasonDeparture(payload)
      .then(() => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_SAVE_SUCCESS);
        dispatch('getSeasonDepartures');
        return Promise.resolve(true);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_SAVE_FAILURE, err.response.data);
      });
  },

  /**
   * TRANSPORTATION PRICES DEPARTURE EXTRAS
   */

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  getDepartureExtras({ commit, state, dispatch }) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_LOAD_PENDING);
    return FerryService.getExtrasByRoute(state.prices.route)
      .then((res) => {
        if (res.data) {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_LOAD_SUCCESS, res.data);
          dispatch('getDepartureExtrasPrices', {
            seasonDepartureId: state.prices.season.selectedDeparture.id,
          });
        } else {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_LOAD_SUCCESS, []);
        }
      })
      .catch(() => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @param payload
   */
  getDepartureExtrasPrices({ commit }, payload) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_PRICE_PENDING);
    return FerryService.getDepartureExtrasPrices(payload.seasonDepartureId)
      .then((res) => {
        if (res.status === 200) {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_PRICE_SUCCESS, res.data);
        } else {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_PRICE_SUCCESS, []);
        }
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_PRICE_FAILURE, err);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   */
  updateDeparturePriceExtra({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_PRICE_SAVE_PENDING);
    return FerryService.updateDeparturePriceExtra(payload.seasonDepartureId, payload.body)
      .then((res) => {
        if (res.status === 204) {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_PRICE_SAVE_SUCCESS);
          dispatch('getDepartureExtrasPrices', {
            seasonDepartureId: payload.seasonDepartureId,
          });
        } else {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_PRICE_SAVE_FAILURE, res.response.data);
        }
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_PRICE_SAVE_FAILURE, err);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   */
  deleteDeparturePriceExtra({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_PRICE_DELETE_PENDING);
    return FerryService.deleteDeparturePriceExtra(payload.seasonDepartureId, payload.body)
      .then((res) => {
        if (res.status === 204) {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_PRICE_DELETE_SUCCESS, 0);
          dispatch('getDepartureExtrasPrices', {
            seasonDepartureId: payload.seasonDepartureId,
          });
        } else {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_PRICE_DELETE_FAILURE, res.response.data);
        }
      })
      .catch((error) => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_EXTRAS_PRICE_DELETE_FAILURE, error.response.data);
      });
  },

  /**
   * TRANSPORTATION PRICES DEPARTURE DAYS
   */

  /**
   * @param commit
   * @param payload
   */
  getDepartureDays({ commit }, payload) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_DAYS_PENDING);
    return FerryService.getDepartureDays(payload.seasonDepartureId, payload.direction)
      .then((res) => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_DAYS_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_DAYS_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   */
  addDepartureDay({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_DAYS_SAVE_PENDING);
    return FerryService.addDepartureDay(payload.seasonDepartureId, payload.direction, payload.body)
      .then((res) => {
        if (res.status === 204) {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_DAYS_SAVE_SUCCESS);
          dispatch('getDepartureDays', payload);
        } else {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_DAYS_SAVE_FAILURE);
        }
      })
      .catch(() => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_DAYS_SAVE_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   */
  deleteDepartureDay({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_DAYS_DELETE_PENDING);
    return FerryService.deleteDepartureDay(payload.seasonDepartureId, payload.direction, payload.body)
      .then((res) => {
        if (res.status === 204) {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_DAYS_DELETE_SUCCESS);
          dispatch('getDepartureDays', payload);
        } else {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_DAYS_DELETE_FAILURE);
        }
      })
      .catch(() => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_DAYS_DELETE_FAILURE);
      });
  },

  /**
   * TRANSPORTATION PRICES DEPARTURE BLOCK DAYS
   */

  /**
   * @param commit
   * payload
   */
  getDepartureBlockDaysDates({ commit }, payload) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_BLOCK_DAYS_PENDING);
    return FerryService.getDepartureDays(payload.seasonDepartureId, payload.direction)
      .then((res) => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_BLOCK_DAYS_SUCCESS, res.data);
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_BLOCK_DAYS_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param payload
   * @param dispatch
   */
  addDepartureBlockDay({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_BLOCK_DAYS_SAVE_PENDING);
    return FerryService.addDepartureBlockDay(payload.seasonDepartureId, payload.direction, payload.body)
      .then((res) => {
        if (res.status === 204) {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_BLOCK_DAYS_SAVE_SUCCESS);
          dispatch('getDepartureBlockDaysDates', payload);
        } else {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_BLOCK_DAYS_SAVE_FAILURE);
        }
      })
      .catch(() => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_BLOCK_DAYS_SAVE_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   */
  deleteDepartureBlockDay({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_BLOCK_DAYS_DELETE_PENDING);
    return FerryService.deleteDepartureBlockDay(payload.seasonDepartureId, payload.direction, payload.body)
      .then((res) => {
        if (res.status === 204) {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_BLOCK_DAYS_DELETE_SUCCESS);
          dispatch('getDepartureBlockDaysDates', payload);
        } else {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_BLOCK_DAYS_DELETE_FAILURE);
        }
      })
      .catch(() => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_BLOCK_DAYS_DELETE_FAILURE);
      });
  },

  /**
   * TRANSPORTATION PRICES DEPARTURE CABINS
   */

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   */
  getDepartureCabins({ commit, state, dispatch }) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_LOAD_PENDING);
    return FerryService.getCabinsByRoute(state.prices.route)
      .then((res) => {
        if (res.data) {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_LOAD_SUCCESS, res.data);
          dispatch('getDepartureCabinsPrices', {
            seasonDepartureId: state.prices.season.selectedDeparture.id,
            type: 'cabin',
          });
        } else {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_LOAD_NO_CONTENT);
        }
        return Promise.resolve(true);
      })
      .catch(() => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @param payload
   */
  getDepartureCabinsPrices({ commit }, payload) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_PRICES_LOAD_PENDING);
    return FerryService.getCabinsPrices(payload.seasonDepartureId, payload.type)
      .then((res) => {
        if (res.status === 200) {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_PRICES_LOAD_SUCCESS, res.data);
        } else {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_PRICES_LOAD_SUCCESS, []);
        }
      })
      .catch(() => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_PRICES_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   */
  updateDeparturePriceCabin({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_PRICE_SAVE_PENDING);
    return FerryService.updateDeparturePriceCabin(payload.seasonDepartureId, payload.body)
      .then((res) => {
        if (res.status === 204) {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_PRICE_SAVE_SUCCESS);
          dispatch('getDepartureCabins');
        } else {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_PRICE_SAVE_FAILURE, res.response.data);
        }
      })
      .catch((err) => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_PRICE_SAVE_PENDING, err);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   */
  deleteDeparturePriceCabin({ commit, dispatch }, payload) {
    commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_PRICE_DELETE_PENDING);
    return FerryService.deleteDeparturePriceCabin(payload.seasonDepartureId, payload.body)
      .then((res) => {
        if (res.status === 204) {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_PRICE_DELETE_SUCCESS);
          dispatch('getDepartureCabins');
        } else {
          commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_PRICE_DELETE_FAILURE, res.response.data);
        }
      })
      .catch((error) => {
        commit(C.TRANSPORTATION_PRICES_DEPARTURE_CABINS_PRICE_DELETE_FAILURE, error.response.data);
      });
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/transportation/actions.js